import React, { useCallback, useEffect, useState } from 'react';

// COMPONENTS
import Icon from '../Icon';
import Skeleton from '../Skeleton';
import Tag from '../Tag';
import Tooltip from '../Tooltip';

// ENUMS
import { Icons } from '../../enums/icons';
import { Sizes } from '../../enums/sizes';
import { SkeletonTypes } from '../../enums/skeletonTypes';
import { TimelineItemType } from '../../enums/timeline';
import { TooltipOrigins } from '../../enums/tooltips';

// INTERFACES
import {
  ITimeline,
  ITimelineFooter,
  ITimelineItem,
  ITimelineItemAction,
  ITimelineStep
} from '../../interfaces/Timeline.interface';

// STYLES
import {
  Content,
  QtyDays,
  TilelineMessage,
  TimelineContentContainerStyled,
  TimelineContentItemContainerHeaderStyled,
  TimelineContentItemContainerStyled,
  TimelineContentItemHeaderStyled,
  TimelineContentItemStyled,
  TimelineContentStyled,
  TimelineEmptyContainer,
  TimelineEmptyItem,
  TimelineEmptyStyled,
  TimelineEmptySubTitle,
  TimelineEmptyTitle,
  TimelineFooterBadgeStyled,
  TimelineFooterInfoStyled,
  TimelineFooterStyled,
  TimelineFooterSubTitleStyled,
  TimelineFooterTitleStyled,
  TimelineLoadingStyled,
  TimelineStyled,
  TimelineSubTitle,
  TimelineTitle,
  Title
} from './styles';

// UTILS
import { flatMap, renderHtml } from '../../utils/html';
import { isExternalLink } from '../../utils/url';
import copyClipboard from '../../utils/copy-clipboard';

const Timeline: React.FC<ITimeline> = ({
  firstStepText,
  isLoading,
  isMobile,
  items = [],
  onClick,
  title
}: ITimeline) => {
  const [openedTimeline, setOpenedTimeline] = useState({
    [0]: {
      opened: true,
      items: { [0]: true }
    }
  });
  const [isExpanded, setIsExpanded] = useState(!isMobile);
  const [timelineItems, setTimelineItems] = useState(items);

  useEffect(() => {
    if (items.length > 0) {
      if (isExpanded) {
        setTimelineItems(items);
      } else {
        const newItems = items;
        setTimelineItems(newItems.slice(0, 1));
      }
    }
  }, [items, isExpanded]);

  const handleOpenedTimeline = useCallback(
    (index: number, idx: number = -1) => {
      let newOpenedTimeline = openedTimeline;

      if (!newOpenedTimeline[index]) {
        setOpenedTimeline({
          ...openedTimeline,
          [index]: {
            opened: true,
            items: []
          }
        });
        return;
      }

      if (idx >= 0) {
        setOpenedTimeline({
          ...openedTimeline,
          [index]: {
            ...openedTimeline[index],
            items: {
              ...openedTimeline[index].items,
              [idx]: !openedTimeline[index].items[idx]
            }
          }
        });
        return;
      }

      setOpenedTimeline({
        ...openedTimeline,
        [index]: {
          opened: !openedTimeline[index].opened,
          items: []
        }
      });
    },
    [openedTimeline]
  );

  const InitTimeline = () => {
    return (
      <>
        {isExpanded && (
          <TimelineContentItemStyled>
            <TimelineContentItemHeaderStyled isOpened={false} type={TimelineItemType.INIT}>
              {firstStepText}
            </TimelineContentItemHeaderStyled>
          </TimelineContentItemStyled>
        )}
        {isMobile && (
          <TimelineContentItemStyled onClick={() => setIsExpanded(!isExpanded)}>
            <TimelineContentItemHeaderStyled
              isExpandItem={true}
              isOpened={false}
              isExpanded={isExpanded}
              type={TimelineItemType.EXPAND}
            >
              {`Ver ${!isExpanded ? 'mais' : 'menos'}`}
            </TimelineContentItemHeaderStyled>
          </TimelineContentItemStyled>
        )}
      </>
    );
  };

  const TimelineEmpty = () => {
    return (
      <TimelineEmptyContainer>
        <TimelineEmptyStyled>
          <TimelineEmptyItem />
          <TimelineEmptyItem />
          <TimelineEmptyItem />
        </TimelineEmptyStyled>
        <TimelineEmptyTitle>Em breve!</TimelineEmptyTitle>
        <TimelineEmptySubTitle>Logo você poderá acompanhar cada passo da sua proposta.</TimelineEmptySubTitle>
      </TimelineEmptyContainer>
    );
  };

  const TimelineLoading = () => {
    return (
      <TimelineContentStyled isLoading={true}>
        <TimelineLoadingStyled>
          <Skeleton type={SkeletonTypes.TIMELINE_INDICATOR} />
          <Skeleton type={SkeletonTypes.TEXT} width={134} height={16} />
          <Skeleton type={SkeletonTypes.TEXT} width={100} height={16} />
        </TimelineLoadingStyled>

        <TimelineLoadingStyled>
          <Skeleton type={SkeletonTypes.TIMELINE_INDICATOR} />
          <Skeleton type={SkeletonTypes.TEXT} width={134} height={16} />
          <Skeleton type={SkeletonTypes.TEXT} width={100} height={16} />
        </TimelineLoadingStyled>

        <TimelineLoadingStyled>
          <Skeleton type={SkeletonTypes.TIMELINE_INDICATOR} />
          <Skeleton type={SkeletonTypes.TEXT} width={134} height={16} />
          <Skeleton type={SkeletonTypes.TEXT} width={100} height={16} />
        </TimelineLoadingStyled>
      </TimelineContentStyled>
    );
  };

  const renderFooter = (footer: ITimelineFooter) => {
    return (
      <TimelineFooterStyled>
        <TimelineFooterBadgeStyled>{footer.badge}</TimelineFooterBadgeStyled>
        <TimelineFooterInfoStyled>
          <TimelineFooterTitleStyled>{footer.title}</TimelineFooterTitleStyled>
          <TimelineFooterSubTitleStyled>{footer.subtitle}</TimelineFooterSubTitleStyled>
        </TimelineFooterInfoStyled>
      </TimelineFooterStyled>
    );
  };

  const renderMessage = (message: any, action?: ITimelineItemAction) => {
    if (!action || !onClick) {
      return <span {...renderHtml(message)} />;
    }

    let result = message;
    let component = <a onClick={() => !action.disabled && onClick(action.action)}>{action.label}</a>;

    if (isExternalLink(action.action)) {
      component = (
        <a href={action.action} rel="noreferrer" target="_blank">
          {action.label}
        </a>
      );
    }

    result = flatMap(result.split('{action}'), (part: any, idx: number) => {
      const newComponent = <React.Fragment key={`flat-map-${idx}`}>{component}</React.Fragment>;
      return [part, newComponent];
    });

    result.pop();

    return result;
  };

  const renderCopyClipboard = (action?: ITimelineItemAction) => {
    if (!action || !isExternalLink(action.action)) {
      return;
    }

    return (
      <Tooltip title="Copiar link" percentAlignSpan="10px" origin={TooltipOrigins.TIMELINE}>
        <div onClick={() => copyClipboard(action.action)}>
          <Icon type={Icons.COPYCLIPBOARD} />
        </div>
      </Tooltip>
    );
  };

  const renderTimelineContent = ({
    action,
    color,
    footer,
    message,
    spotlight,
    subtitle,
    title,
    customMessage
  }: ITimelineStep) => {
    return (
      <TimelineContentItemContainerStyled color={color}>
        <TimelineContentItemContainerHeaderStyled>
          <TimelineTitle>{title}</TimelineTitle>
          {subtitle && <TimelineSubTitle>{subtitle}</TimelineSubTitle>}
        </TimelineContentItemContainerHeaderStyled>
        <TilelineMessage isDisabledAction={!!action?.disabled}>
          {spotlight && <span {...renderHtml(spotlight)} />}
          {customMessage && renderMessage(customMessage)}
          {renderMessage(message ? message.replace(/\n/g, '<br/>') : '', action)}
          {renderCopyClipboard(action)}
        </TilelineMessage>
        {footer && renderFooter(footer)}
      </TimelineContentItemContainerStyled>
    );
  };

  const TimelineContent = () => {
    return (
      <TimelineContentStyled>
        {timelineItems.map((item: ITimelineItem, index: number) => {
          const isCurrentItem = index === 0;
          const openedItem = openedTimeline[index];

          return (
            <TimelineContentItemStyled key={`tl-${index}`}>
              <TimelineContentItemHeaderStyled
                isHeader
                isOpened={openedItem?.opened}
                onClick={() => handleOpenedTimeline(index)}
                type={isCurrentItem ? TimelineItemType.CURRENT : TimelineItemType.DEFAULT}
              >
                {item.title}
                {item.qtyDays && <QtyDays>{item.qtyDays}</QtyDays>}
              </TimelineContentItemHeaderStyled>
              <TimelineContentContainerStyled isOpened={openedItem?.opened}>
                {item.timelineSteps?.map((step: ITimelineStep, idx: number) => {
                  const isCurrentStep = idx === 0;
                  const openedStep = (openedItem?.items || [])[idx];

                  return (
                    <TimelineContentItemStyled key={`tl-step-${idx}`}>
                      <TimelineContentItemHeaderStyled
                        isOpened={openedStep}
                        onClick={() => handleOpenedTimeline(index, idx)}
                        type={isCurrentStep && isCurrentItem ? TimelineItemType.CURRENT : TimelineItemType.DEFAULT}
                      >
                        {step.label}
                        {step.badge && (
                          <Tag color={isCurrentStep ? step.badge.color : undefined} size={Sizes.SMALL}>
                            {step.badge.label}
                          </Tag>
                        )}
                      </TimelineContentItemHeaderStyled>

                      <TimelineContentContainerStyled isOpened={openedStep}>
                        {(step.items || []).length > 0 ? (
                          <>
                            {step.items?.map((it: ITimelineStep) => renderTimelineContent(it))}
                            {step.finishTask && (
                              <TimelineContentItemContainerStyled isFinishedTask {...renderHtml(step.finishTask)} />
                            )}
                          </>
                        ) : (
                          renderTimelineContent(step)
                        )}
                      </TimelineContentContainerStyled>
                    </TimelineContentItemStyled>
                  );
                })}
              </TimelineContentContainerStyled>
            </TimelineContentItemStyled>
          );
        })}
        <InitTimeline />
      </TimelineContentStyled>
    );
  };

  return (
    <TimelineStyled>
      {isLoading ? <Skeleton type={SkeletonTypes.TEXT} width={139} height={14} /> : <Title>{title}</Title>}
      <Content>
        {isLoading && <TimelineLoading />}
        {!isLoading && (items.length === 0 ? <TimelineEmpty /> : <TimelineContent />)}
      </Content>
    </TimelineStyled>
  );
};

export default Timeline;
